import * as React from 'react';
import { Validated } from './Validated';

interface IInputProps {
  /** inputのid。省略したときは部品の中で一意な値を作る */
  id?: string;
  name?: string;
  label?: string;
  type?: string;
  className?: string;
  placeholder?: string;
  /** trueのとき、ラベルの後ろに必須を表す赤い「*」を出す */
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** 入力欄から離れたときに呼ぶ */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** 入力候補として出すdatalistのid */
  list?: string;
  validationMessage?: string;
  value?: string | number;
  accept?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
}

let generatedInputIdCount = 0;

/**
 * 部品の一生のあいだ変わらない一意なidを返す。
 * React 16にはuseIdがないため、最初の描画で連番からidを作り、useRefに保持して以降の描画で使い回す。
 */
function useGeneratedId(prefix: string): string {
  const idRef = React.useRef<string>();
  if (idRef.current === undefined) {
    generatedInputIdCount += 1;
    idRef.current = `${prefix}-${generatedInputIdCount}`;
  }
  return idRef.current;
}

/** エラーメッセージが出ているときだけ、入力欄からメッセージの要素を参照させる */
function getDescribedBy(validationMessage: string | undefined, messageId: string): string | undefined {
  if (validationMessage) {
    return messageId;
  }
  return undefined;
}

export const Input: React.FC<IInputProps> = function (props) {
  const generatedId = useGeneratedId('Input');
  const inputId = props.id ?? generatedId;
  const messageId = `${inputId}-message`;

  const describedBy = getDescribedBy(props.validationMessage, messageId);

  return (
    <div className="App-input_container">
      {(() => {
        if (!props.label) {
          return null;
        }
        return (
          <label className="App-label" htmlFor={inputId}>
            {props.label}
            {(() => {
              if (!props.required) {
                return null;
              }
              return (
                <span className="App-label_required" aria-hidden="true">
                  *
                </span>
              );
            })()}
          </label>
        );
      })()}
      <Validated message={props.validationMessage} messageId={messageId}>
        <input
          id={inputId}
          name={props.name}
          className={props.className}
          type={props.type}
          placeholder={props.placeholder}
          aria-required={props.required}
          aria-invalid={Boolean(props.validationMessage)}
          aria-describedby={describedBy}
          onChange={props.onChange}
          onBlur={props.onBlur}
          list={props.list}
          value={props.value}
          accept={props.accept}
          min={props.min}
          step={props.step}
          disabled={props.disabled}
        />
      </Validated>
    </div>
  );
};

interface ICheckboxProps {
  onChange?: (value: boolean) => void;
  value?: boolean;
  disabled?: boolean;
}

export const Checkbox: React.FC<ICheckboxProps> = (props) => {
  return (
    <input
      type="checkbox"
      checked={props.value}
      disabled={props.disabled}
      onChange={(event) => props.onChange?.(event.target.checked)}
    />
  );
};

interface ISelectorProps {
  label?: string;
}

export const Selector: React.FC<ISelectorProps> = function (props) {
  return (
    <div className="App-input_container">
      <select>{props.children}</select>
    </div>
  );
};

interface ILabelProps {
  label: string;
}

export const Label: React.FC<ILabelProps> = function (props) {
  return <label className="App-label">{props.label}</label>;
};

interface IDeletableInputProps extends IInputProps {
  onDelete?: () => void;
}

export const DeletableInput: React.FC<IDeletableInputProps> = function (props) {
  return (
    <div className="App-input_container">
      {(() => {
        if (props.label) {
          return;
        }
      })()}
      <input
        className={props.className}
        type={props.type}
        onChange={props.onChange}
        value={props.value}
      />
      <button onClick={() => props.onDelete?.()}>削除</button>
    </div>
  );
};
